import {getTestProps} from '@benchling/js-test-utils';
import {Anchor, Box, createStyles, Paper, Stack, Text} from '@mantine/core';
import React, {useCallback, useLayoutEffect, useRef, useState} from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';

import {remarkGfmTables} from 'ui-library/remarkGfmTables';
import {readableTime} from 'util/DateUtil';

const DESCRIPTION_COLLAPSE_THRESHOLD_LINES = 15;
const DESCRIPTION_CONTENT_ID = 'agent-foundry-description-content';
const DESCRIPTION_LINE_HEIGHT_PX = 24;
const MAX_COLLAPSED_DESCRIPTION_LINES = 10;
const DESCRIPTION_MARKDOWN_COMPONENTS = {
  h1: 'h5',
  h2: 'h5',
  h3: 'h5',
  h4: 'h5',
  h5: 'h5',
  h6: 'h5',
} as const;

export function getCollapsedDescriptionLineCount(expandedLineCount: number): number | null {
  if (expandedLineCount <= DESCRIPTION_COLLAPSE_THRESHOLD_LINES) {
    return null;
  }
  return Math.min(Math.floor(expandedLineCount / 2), MAX_COLLAPSED_DESCRIPTION_LINES);
}

const useStyles = createStyles((theme) => ({
  container: {
    margin: '0 auto',
    maxWidth: 1000,
    width: '100%',
  },
  descriptionCard: {
    padding: theme.spacing.xl,
  },
  descriptionClip: {
    overflow: 'hidden',
  },
  descriptionContent: {
    fontSize: theme.fontSizes.md,
    lineHeight: `${DESCRIPTION_LINE_HEIGHT_PX}px`,
    overflowWrap: 'anywhere',
    '& > :first-child': {
      marginTop: 0,
    },
    '& > :last-child': {
      marginBottom: 0,
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      color: theme.colors.gray[7],
      fontSize: theme.fontSizes.sm,
      fontWeight: 600,
      letterSpacing: '0.02em',
      lineHeight: `${DESCRIPTION_LINE_HEIGHT_PX}px`,
      marginBottom: theme.spacing.xs,
      marginTop: theme.spacing.lg,
      textTransform: 'uppercase',
    },
    '& p': {
      marginBottom: theme.spacing.md,
      marginTop: 0,
    },
    '& ul, & ol': {
      marginBottom: theme.spacing.md,
      marginTop: 0,
      paddingLeft: theme.spacing.lg,
    },
    '& li': {
      marginBottom: theme.spacing.xs,
      paddingLeft: theme.spacing.xs,
    },
  },
  descriptionToggle: {
    alignSelf: 'flex-start',
  },
  detailsList: {
    display: 'grid',
    gap: theme.spacing.xl,
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    margin: 0,
    paddingTop: theme.spacing.md,
    [`@media (max-width: ${theme.breakpoints.xs})`]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  detailsValue: {
    margin: 0,
    overflowWrap: 'anywhere',
  },
}));

interface AgentFoundryDescriptionProps {
  description: string;
}

function AgentFoundryDescription({description}: AgentFoundryDescriptionProps): JSX.Element {
  const {classes} = useStyles();
  const descriptionContentRef = useRef<HTMLDivElement>(null);
  const [expandedLineCount, setExpandedLineCount] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const measureDescription = useCallback(() => {
    const descriptionContent = descriptionContentRef.current;
    if (descriptionContent == null) {
      return;
    }
    setExpandedLineCount(Math.ceil(descriptionContent.scrollHeight / DESCRIPTION_LINE_HEIGHT_PX));
  }, []);

  useLayoutEffect(() => {
    measureDescription();
    const descriptionContent = descriptionContentRef.current;
    if (descriptionContent == null || typeof ResizeObserver === 'undefined') {
      return;
    }
    const resizeObserver = new ResizeObserver(measureDescription);
    resizeObserver.observe(descriptionContent);
    return () => {
      resizeObserver.disconnect();
    };
  }, [description, measureDescription]);

  const collapsedLineCount =
    expandedLineCount == null ? null : getCollapsedDescriptionLineCount(expandedLineCount);
  const isCollapsible = collapsedLineCount != null;
  const maxHeight =
    isCollapsible && !isExpanded ? `${collapsedLineCount * DESCRIPTION_LINE_HEIGHT_PX}px` : undefined;
  const isCollapsed = isCollapsible && !isExpanded;

  // Clipping alone leaves offscreen links keyboard-accessible. Inert keeps the collapsed preview
  // out of the focus order and accessibility tree until the user expands the description.
  useLayoutEffect(() => {
    descriptionContentRef.current?.toggleAttribute('inert', isCollapsed);
  }, [isCollapsed]);

  const handleToggle = (): void => {
    setIsExpanded((expanded) => !expanded);
  };

  return (
    <Stack spacing="xs">
      <Text component="h4" size="sm" weight={600}>
        Description
      </Text>
      <Paper withBorder={true} radius="md" className={classes.descriptionCard}>
        {description.length === 0 ? (
          <Text c="dimmed">No description yet.</Text>
        ) : (
          <Stack spacing="sm">
            <Box
              className={classes.descriptionClip}
              style={{maxHeight}}
              {...getTestProps({component: 'AgentFoundryDetails', element: 'descriptionClip'})}
            >
              <Box
                id={DESCRIPTION_CONTENT_ID}
                ref={descriptionContentRef}
                className={classes.descriptionContent}
                role="region"
                aria-label="Description content"
                {...getTestProps({component: 'AgentFoundryDetails', element: 'descriptionContent'})}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfmTables]}
                  rehypePlugins={[rehypeSanitize]}
                  components={DESCRIPTION_MARKDOWN_COMPONENTS}
                >
                  {description}
                </ReactMarkdown>
              </Box>
            </Box>
            {isCollapsible && (
              <Anchor
                component="button"
                type="button"
                size="sm"
                className={classes.descriptionToggle}
                aria-controls={DESCRIPTION_CONTENT_ID}
                aria-expanded={isExpanded}
                onClick={handleToggle}
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </Anchor>
            )}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}

export interface AgentFoundryDetailsProps {
  createdAt: string;
  creatorName: string | null;
  description: string;
  id: string;
  modifiedAt: string;
}

export default function AgentFoundryDetails({
  createdAt,
  creatorName,
  description,
  id,
  modifiedAt,
}: AgentFoundryDetailsProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();

  return (
    <Stack
      spacing="xl"
      className={classes.container}
      {...getTestProps({component: 'AgentFoundryDetails', element: 'root'})}
    >
      <Box component="dl" className={classes.detailsList}>
        <Stack spacing="xs">
          <Text component="dt" size="sm" weight={600}>
            Created by
          </Text>
          <Text component="dd" className={classes.detailsValue}>
            {creatorName ?? '—'}
          </Text>
        </Stack>
        <Stack spacing="xs">
          <Text component="dt" size="sm" weight={600}>
            Foundry ID
          </Text>
          <Text component="dd" className={classes.detailsValue}>
            {id}
          </Text>
        </Stack>
        <Stack spacing="xs">
          <Text component="dt" size="sm" weight={600}>
            Created
          </Text>
          <Text component="dd" className={classes.detailsValue}>
            {readableTime(createdAt)}
          </Text>
        </Stack>
        <Stack spacing="xs">
          <Text component="dt" size="sm" weight={600}>
            Modified
          </Text>
          <Text component="dd" className={classes.detailsValue}>
            {readableTime(modifiedAt)}
          </Text>
        </Stack>
      </Box>
      <AgentFoundryDescription description={description} />
    </Stack>
  );
}
